import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useGrowthBook } from '@growthbook/growthbook-react';
import moment from 'moment';
import {
  IconButton,
  TextField,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  InputAdornment,
  Popper,
  Fade,
  Paper,
  // Button
} from '@mui/material';
import {
  RestartAltOutlined,
  Close,
  DeleteOutlineOutlined,
  DownloadOutlined,
  Search,
} from '@mui/icons-material';
import CalendarMonthRounded from '@src/assets/icons/CalendarMonthRounded.svg';
import CalendarActive from '@src/assets/icons/CalendarActive.svg';
import FilterActive from '@src/assets/icons/FilterActive.svg';
import FilterAltFilled from '@src/assets/icons/FilterAltFilled.svg';
import CircleBlue from '@src/assets/icons/circleblue.svg';
import Tooltip from '@src/components/Tooltip';
import CustomDatePickerRange from '@src/components/CustomDatePickerRange';
import { REQUEST_STATUS } from '@src/constants/voice';
import { PAGINATION_LIMIT, PAGINATION_LIMIT_REQUEST } from '@src/constants';
import { FETCH_REQUESTS_INTERVAL } from '@src/constants/websocket';
import debounce from '@src/utils/debounce';
import apis from '@src/apis';
import { COLOR } from '@src/styles/color';
// import arrowIconUp from '@src/assets/icons/arrow-icon-up.png';

import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { TTS_GUIDE } from '@src/constants/tourGuide';
import NotificationDialog from '@src/components/NotificationDialog';
import actions from '@src/redux/actions';
import {
  FIELDS_FILTER,
  MAX_AUDIO_DOWNLOAD,
  MAX_BODY_CONTENT_HEIGHT,
  MAX_REQUEST_DELETE,
  // REQUEST_TYPE,
} from '@src/constants/request';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { downloadAudioLinks } from '@src/apis/requests';
import RequestTable from '@src/containers/TTSNew/NewRequest/RequestTable';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { StyledRequests } from '@src/containers/TTSNew/NewRequest/index.style';
import { FEATURE } from '@src/constants/package';
import { checkFeaturePermission } from '@src/services/tts';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import RequestsActionHeader from '@src/containers/TTSNew/NewRequest/RequestsActionHeader';
import { StyledTableCell } from '@src/components/NewTable/index.style';
import { StyledActionWarning } from '../DubbingSentences/index.style';

const initialFilter = {
  status: '',
  createdAt: [null, null],
};

const ListDubbingNewVersion = ({
  showNotiOldVersion,
  requestLoading,
  onChangeRequestLoading,
  isExpand,
  onChangeIsExpand,
  usePanelResize,
}) => {
  const [requests, setRequests] = useState([]);
  const [totalRequests, setTotalRequests] = useState(0);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(initialFilter);
  const [sort, setSort] = useState('createdAt_desc');
  const [loading, setLoading] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState(false);
  const [validateDeleteRequest, setValidateDeleteRequest] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [allowDownload, setAllowDownload] = useState(false);
  const [selectedFields, setSelectedFields] = useState(
    Object.values(FIELDS_FILTER),
  );

  const [anchorFieldFilter, setAnchorFieldFilter] = useState(null);
  const [anchorStatusRequest, setAnchorStatusRequest] = useState(null);
  const [openPopperStatusRequest, setOpenPopperStatusRequest] = useState(false);
  const [activeResetButton, setActiveResetButton] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  const refClickOutSide = useClickOutside(() =>
    setOpenPopperStatusRequest(false),
  );
  const [numSelected, setNumSelected] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_LIMIT);
  const [requestsInfinite, setRequestsInfinite] = useState([]);

  const { usingPackage, ttsUser } = useSelector((state) => state.user);

  const isStudioHaveSubtitleFeature = useMemo(
    () =>
      usingPackage?.id &&
      (checkFeaturePermission(usingPackage?.features, FEATURE.SUBTITLE) ||
        checkFeaturePermission(ttsUser?.features, FEATURE.SUBTITLE)),

    [usingPackage],
  );

  const growthbook = useGrowthBook();
  const useOldDownloadAll = growthbook.getFeatureValue(
    FEATURE_KEYS.DOWNLOAD_AUDIO_LINK,
  );

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const requestsRef = useRef(requests);
  const filterRef = useRef(filter);
  const sortRef = useRef(sort);

  const responseRequest = useSelector((state) => state.request);
  const { user } = useSelector((state) => state.auth);

  const handleUpdateRequests = (newRequests) => {
    setRequests(newRequests);
  };
  const { getFeatureValue } = useFeatureFlags();

  const isProcessDubbingByUnitSecond = getFeatureValue(
    FEATURE_KEYS.DUBBING_BY_UNIT_SECOND,
    { userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );

  const useNewDubbingUi = getFeatureValue(FEATURE_KEYS.NEW_DUBBING_UI, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const useInfiniteScrollRequests = getFeatureValue(
    FEATURE_KEYS.INFINITE_SCROLL_REQUESTS,
    {
      userId: user?.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const useNewPagination = getFeatureValue(FEATURE_KEYS.NEW_PAGINATION, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const [page, setPage] = useState(useNewPagination ? 0 : 1);
  const pageRef = useRef(page);

  const REQUEST_HEIGHT = 70;

  const pageLimit = useNewDubbingUi
    ? Math.floor((window.innerHeight * 0.8 - 68) / REQUEST_HEIGHT)
    : PAGINATION_LIMIT;

  const handleChangePage = (newPage, scrolling = false) => {
    setIsScrolling(scrolling);
    setPage(newPage);
  };
  const handleChangeFilter = (name, value) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
    setPage(useNewPagination ? 0 : 1);
  };

  const handleChangeSort = (newSort) => {
    setSort(newSort);
    setPage(0);
  };

  const handleResetFilter = () => {
    setFilter(initialFilter);
    setSearch('');
    setPage(useNewPagination ? 0 : 1);
  };

  const handleChangeSearch = (e) => {
    setSearch(e.target.value);
    setPage(useNewPagination ? 0 : 1);
  };

  const handleSelectFields = (field) => {
    if (selectedFields.includes(field)) {
      setSelectedFields(selectedFields.filter((f) => f !== field));
    } else setSelectedFields([...selectedFields, field]);
  };

  const handleChangeDatePickerRange = (value) =>
    handleChangeFilter('createdAt', value);

  const offsetRequest = useMemo(() => {
    if (useNewPagination) {
      return page * rowsPerPage;
    }
    if (useInfiniteScrollRequests) {
      return page * PAGINATION_LIMIT_REQUEST;
    }
    return (page - 1) * PAGINATION_LIMIT;
  }, [rowsPerPage, page, useNewPagination, useInfiniteScrollRequests]);

  const fetchRequests = async (hasLoading) => {
    if (hasLoading) setLoading(true);
    const { status, createdAt } = filterRef.current;

    const data = await apis.dubbing.getDubbingRequests({
      offset: offsetRequest,
      limit: useNewPagination ? rowsPerPage : pageLimit,
      sort,
      search,
      status,
      startDate: createdAt[0]
        ? moment(createdAt[0]).startOf('day').toISOString()
        : undefined,
      endDate: createdAt[1]
        ? moment(createdAt[1]).endOf('day').toISOString()
        : undefined,
      fields:
        'id,title,characters,seconds,createdAt,progress,status,voice,audioType,audioLink,retentionPeriod,processingAt',
      notExistsProject: true,
    });

    if (data && data.status) {
      const newRequests = data.result.requests.map((request) => {
        const req = requests.find((r) => r.id === request.id);
        if (req) {
          return {
            ...request,
            progress:
              req?.progress && request.progress < req?.progress
                ? req.progress
                : request.progress,
          };
        }
        return request;
      });

      onChangeRequestLoading(false);
      setLoading(false);
      setRequests(newRequests);
      if (isScrolling) {
        setRequestsInfinite((prev) => [...prev, ...newRequests]);
        setIsScrolling(false);
      } else {
        setRequestsInfinite(newRequests);
      }
      setTotalRequests(data.result.metadata.total);
    }
  };

  const fetchProgressRequest = async (requestId) => {
    const data = await apis.requests.getProgressRequest(requestId);
    if (data && data.status) {
      const newRequests = requestsRef.current.map((request) => {
        if (request.id === requestId) {
          const { progress, status, audioLink } = data.result;
          return {
            ...request,
            progress,
            status,
            audioLink,
          };
        }
        return request;
      });
      handleUpdateRequests(newRequests);
    }
  };

  const handleCloseDeleteWarningDialog = () => {
    setDeleteWarning(false);
  };

  const handleCloseValidateDeleteRequestDialog = () => {
    setValidateDeleteRequest(false);
  };

  const handleCloseFieldsFilter = () => setAnchorFieldFilter(null);

  const handleDeleteSelectRequest = () => {
    const numSelectedRequest = selectedRequests.length;
    if (numSelectedRequest > 0 && numSelectedRequest <= MAX_REQUEST_DELETE) {
      setDeleteWarning(true);
    } else {
      setValidateDeleteRequest(true);
    }
  };

  const handleDownloadAudios = async () => {
    if (selectedRequests.length > MAX_AUDIO_DOWNLOAD) {
      setValidateDeleteRequest(true);
      return;
    }
    if (useOldDownloadAll) {
      const listAudioLink = selectedRequests.map((req) => req.audioLink);
      downloadAudioLinks(listAudioLink);
      return;
    }
    const listPresignedUrl = [];
    const listAudioId = selectedRequests.map((req) => ({
      id: req.id,
      title: req.title,
    }));
    const promises = listAudioId.map(async ({ id, title }) => {
      try {
        const res = await apis.requests.getPresignedAudioUrl(id);
        if (res?.status) {
          listPresignedUrl.push(res.result?.audio);
        } else {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message: 'downloadError',
              value: title,
            }),
          );
        }
      } catch (error) {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: 'downloadError',
            value: title,
          }),
        );
      }
    });

    await Promise.all(promises);
    downloadAudioLinks(listPresignedUrl);
  };

  const requestsDataTable = useMemo(() => {
    if (useInfiniteScrollRequests) {
      return requestsInfinite;
    }
    return requests;
  }, [requests, requestsInfinite, useInfiniteScrollRequests]);

  const handleSelectRequest = (selectReqs) => {
    setSelectedRequests(selectReqs);
    const allowDownloadAudios = selectReqs.every((req) => req.audioLink);
    setAllowDownload(allowDownloadAudios);
  };

  const handleDeleteRequestConfirm = async () => {
    try {
      const requestIds = selectedRequests.map((req) => req.id);
      const data = await apis.requests.deleteRequest({ requestIds });
      if (!data?.status) {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: 'deleteRequestFailure',
          }),
        );
        setDeleteWarning(false);
        return;
      }
      dispatch(
        actions.noti.push({
          severity: 'success',
          message: 'deleteRequestSuccess',
        }),
      );
      setDeleteWarning(false);
      setSelectedRequests([]);
      setPage(useNewPagination ? 0 : 1);
      fetchRequests(false);
    } catch (error) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: error?.response?.data?.error_message,
        }),
      );
      setDeleteWarning(false);
    }
  };
  const handleSelectAll = (event, checked = false) => {
    if (event.target.checked || checked) {
      const newSelected = requestsDataTable.map((rowData) => {
        const {
          id,
          title,
          audioLink,
          status,
          retentionPeriod,
          createdAt,
          latestRequestId,
        } = rowData;
        return {
          id,
          title,
          audioLink,
          status,
          retentionPeriod,
          createdAt,
          latestRequestId,
        };
      });

      setNumSelected(newSelected.length);
      if (handleSelectRequest)
        if (useInfiniteScrollRequests) {
          handleSelectRequest([...newSelected]);
        } else {
          handleSelectRequest([
            ...new Map(
              [...selectedRequests, ...newSelected].map((item) => [
                item.id,
                item,
              ]),
            ).values(),
          ]);
        }

      return;
    }

    if (handleSelectRequest) {
      const newSelected = selectedRequests.filter(
        (rowSelected) =>
          !requestsDataTable
            .map((rowData) => rowData.id)
            .includes(rowSelected.id),
      );
      handleSelectRequest(newSelected);
      setNumSelected(0);
    }
  };

  // const handleDeleteAllRequests = async () => {
  //   const data = await apis.requests.deleteRequest({
  //     isDeleteAll: true,
  //     type: REQUEST_TYPE.DUBBING,
  //   });
  //   if (!data?.status) {
  //     dispatch(
  //       actions.noti.push({
  //         severity: 'error',
  //         message: 'deleteRequestFailure',
  //       }),
  //     );
  //     return;
  //   }
  //   dispatch(
  //     actions.noti.push({
  //       severity: 'success',
  //       message: 'deleteRequestSuccess',
  //     }),
  //   );
  //   setSelectedRequests([]);
  //   setPage(1);
  //   fetchRequests(false);
  // };

  const updateInprogressRequest = (requestIds) => {
    Promise.all(
      requestIds.map(async (requestId) => {
        await fetchProgressRequest(requestId);
        return null;
      }),
    );
  };

  const updateRequest = (requestId, updateInfo) => {
    const newRequests = requestsRef.current.map((request) => {
      if (request.id === requestId) {
        return {
          ...request,
          ...updateInfo,
        };
      }
      return request;
    });
    handleUpdateRequests(newRequests);
  };

  const renderConfirmDeleteRequestACtion = (handleClose, handleConfirm) => (
    <StyledActionWarning>
      <Button variant="outlined" onClick={handleClose}>
        {t('noValue')}
      </Button>
      <Button variant="contained" onClick={handleConfirm}>
        {t('yesValue')}
      </Button>
    </StyledActionWarning>
  );

  const renderCloseButton = (handleClose) => (
    <Button variant="contained" onClick={handleClose}>
      {t('understood')}
    </Button>
  );

  // const handleBackToTTS = () => {
  //   handleOpenRequest(false);
  //   setPage(1);
  //   handleResetFilter();
  // };

  useEffect(() => {
    requestsRef.current = requests;
  }, [requests]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    sortRef.current = sort;
  }, [sort]);

  useEffect(() => {
    fetchRequests(true);
  }, [page, filter, sort]);

  useEffect(() => {
    debounce(fetchRequests, 500)(search);
  }, [search]);

  useEffect(() => {
    if (requestLoading) fetchRequests(false);
  }, [requestLoading]);

  useEffect(() => {
    const newRequests = requests.map((request) => {
      const isInProgressRequest =
        request.status === REQUEST_STATUS.IN_PROGRESS &&
        responseRequest[request.id];
      if (isInProgressRequest) {
        request.status = responseRequest[request.id].status;
        request.audioLink = responseRequest[request.id].audioLink;
        request.progress = responseRequest[request.id].progress;
        request.processingAt = responseRequest[request.id].processingAt;
      }
      return request;
    });
    handleUpdateRequests(newRequests);
  }, [responseRequest]);

  useEffect(() => {
    const fetchRequestsInterval = setInterval(async () => {
      const inProgressRequest = requestsRef.current
        .filter((request) => request.status === REQUEST_STATUS.IN_PROGRESS)
        .map((request) => request.id);
      if (inProgressRequest?.length) updateInprogressRequest(inProgressRequest);
    }, FETCH_REQUESTS_INTERVAL);

    return () => clearInterval(fetchRequestsInterval);
  }, []);
  useEffect(() => {
    const { status, createdAt } = filter;
    const { status: defaultStatus } = initialFilter;

    const isFilterStatus = status !== defaultStatus;
    const isFilterDate = createdAt[0] || createdAt[1];
    const isFilterSearch = search.trim() !== '';
    const pageCurrent = useNewPagination ? page !== 0 : page !== 1;
    const isFilter =
      isFilterStatus || isFilterDate || isFilterSearch || pageCurrent;
    setActiveResetButton(isFilter);
  }, [filter, search, page, useNewPagination]);

  useEffect(() => {
    if (useInfiniteScrollRequests) {
      fetchRequests(false);
    } else {
      fetchRequests(true);
    }
  }, [page, rowsPerPage, offsetRequest]);

  const tooltipDateFilter = useMemo(() => {
    const tooltipText = filter?.createdAt
      ?.filter(Boolean)
      ?.map((data) => moment(data).format('DD/MM/YYYY'))
      ?.join(' - ');
    return tooltipText;
  }, [filter?.createdAt]);

  const CLASS_TARGET_EXPAND = 'request-list-title';
  const renderDesktopActionTable = () => {
    if (useNewPagination && !isExpand) {
      return <></>;
    }
    return (
      <div className="action-table">
        <div className="request-filter">
          <div
            data-search-keyword={filter?.title}
            className="filter-item"
            role="button"
            tabIndex="0"
            onClick={(e) => e.stopPropagation()}
          >
            <TextField
              size="small"
              className="text-field-request filter-title"
              variant="outlined"
              hiddenLabel={false}
              value={filter?.title}
              placeholder={t('newFindTitle')}
              onChange={handleChangeSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />

            <div
              className="filter-item"
              role="button"
              tabIndex="0"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="date-field">
                <CustomDatePickerRange
                  value={filter.createdAt}
                  onChange={handleChangeDatePickerRange}
                >
                  <Tooltip arrow title={tooltipDateFilter} placement="top">
                    {filter.createdAt[0] ? (
                      <img
                        style={{ width: '24px' }}
                        src={CalendarActive}
                        alt="CalendarMonthRounded"
                      />
                    ) : (
                      <img
                        src={CalendarMonthRounded}
                        alt="CalendarMonthRounded"
                      />
                    )}
                  </Tooltip>
                  {!!filter.createdAt[0] && (
                    <img
                      className="circle-btn"
                      src={CircleBlue}
                      alt="FilterAltFilled"
                    />
                  )}
                </CustomDatePickerRange>
              </div>
            </div>
            <Tooltip
              arrow
              title={filter.status ? t(filter.status) : ''}
              placement="top"
            >
              <IconButton
                className="filter-btn"
                ref={refClickOutSide}
                onClick={(e) => {
                  setAnchorStatusRequest(e.currentTarget);
                  setOpenPopperStatusRequest((prev) => !prev);
                }}
              >
                {filter.status ? (
                  <img
                    style={{ width: '24px' }}
                    src={FilterActive}
                    alt="FilterAltFilled"
                  />
                ) : (
                  <img src={FilterAltFilled} alt="FilterAltFilled" />
                )}

                {filter.status && (
                  <img
                    className="circle-btn"
                    src={CircleBlue}
                    alt="FilterAltFilled"
                  />
                )}
              </IconButton>
            </Tooltip>

            <Popper
              transition
              style={{ zIndex: 1000, width: '180px', color: '#1E164E' }}
              anchorEl={anchorStatusRequest}
              open={openPopperStatusRequest}
              placement="bottom-start"
            >
              {({ TransitionProps }) => (
                <Fade
                  style={{ padding: '5px 0px' }}
                  {...TransitionProps}
                  timeout={350}
                >
                  <Paper>
                    {Object.keys(REQUEST_STATUS).map((item) => (
                      <MenuItem
                        style={{
                          fontSize: '14px',
                          padding: '8px',
                          color: '#1E164E',
                          background:
                            filter?.status === item && COLOR.activeColor,
                        }}
                        onClick={() => {
                          handleChangeFilter('status', item);
                        }}
                        key={item}
                        value={item}
                      >
                        {t(item)}
                      </MenuItem>
                    ))}
                  </Paper>
                </Fade>
              )}
            </Popper>
          </div>

          <Menu
            anchorEl={anchorFieldFilter}
            open={Boolean(anchorFieldFilter)}
            onClose={handleCloseFieldsFilter}
            className="fields-filter"
          >
            {Object.keys(FIELDS_FILTER).map((key) => (
              <MenuItem
                key={key}
                value={FIELDS_FILTER[key]}
                onClick={() => handleSelectFields(FIELDS_FILTER[key])}
              >
                <Checkbox
                  checked={selectedFields.includes(FIELDS_FILTER[key])}
                  onClick={handleSelectFields}
                  style={{ color: COLOR.darkBlue }}
                />
                <div>{t(key)}</div>
              </MenuItem>
            ))}
          </Menu>
          <Tooltip arrow title={t('reset')} placement="bottom">
            <div>
              <IconButton
                className={`filter-btn ${
                  !activeResetButton ? 'disable-color' : 'actived-filter'
                }`}
                onClick={handleResetFilter}
                disabled={!activeResetButton}
              >
                <RestartAltOutlined color="#1E164EA3" />
              </IconButton>
            </div>
          </Tooltip>
        </div>
      </div>
    );
  };

  return (
    <StyledRequests
      onClick={(event) => {
        if (
          typeof event.target.className !== 'string' ||
          (useNewPagination && !!selectedRequests?.length)
        )
          return;
        if (
          event.target.className === CLASS_TARGET_EXPAND ||
          event.target.className?.includes('action-header')
        ) {
          onChangeIsExpand(!isExpand);
        }
      }}
      newPagination={useNewPagination && !isExpand}
      useNewPagination={useNewPagination}
    >
      <div className="request-wrapper" role="button" tabIndex="0">
        <div className="request-list-title">
          {!useNewPagination ? (
            <StyledTableCell
              className="checkbox-cell"
              padding="checkbox"
              align="left"
            >
              <Checkbox
                className="body-checkbox"
                indeterminate={
                  numSelected > 0 && numSelected < requestsDataTable?.length
                }
                checked={
                  requestsDataTable?.length > 0 &&
                  numSelected === requestsDataTable?.length
                }
                onChange={handleSelectAll}
              />
            </StyledTableCell>
          ) : (
            <>
              {!selectedRequests?.length && (
                <div
                  onClick={() => onChangeIsExpand(!isExpand)}
                  role="button"
                  tabIndex="0"
                >
                  <IconButton className="expand-icon" size="small">
                    {isExpand ? (
                      <KeyboardArrowDownRoundedIcon />
                    ) : (
                      <ChevronRightRoundedIcon />
                    )}
                  </IconButton>
                </div>
              )}
              {selectedRequests?.length > 0 && (
                <div
                  onClick={() => setSelectedRequests([])}
                  role="button"
                  tabIndex="0"
                >
                  <IconButton className="expand-icon" size="small">
                    <ClearRoundedIcon />
                  </IconButton>
                </div>
              )}
            </>
          )}

          {!selectedRequests?.length ? (
            <div
              onClick={() => onChangeIsExpand(!isExpand)}
              role="button"
              tabIndex="0"
              className="title-history-requests-wrapper"
            >
              <span className="title-history-requests">{t('listRequest')}</span>

              {!usePanelResize &&
              !useInfiniteScrollRequests &&
              !useNewPagination ? (
                <IconButton className="expand-icon" size="small">
                  {isExpand ? (
                    <KeyboardArrowDownRoundedIcon />
                  ) : (
                    <ChevronRightRoundedIcon />
                  )}
                </IconButton>
              ) : (
                !useNewPagination && (
                  <IconButton className="expand-icon" size="small">
                    <KeyboardArrowDownRoundedIcon />
                  </IconButton>
                )
              )}
            </div>
          ) : (
            <RequestsActionHeader
              isShowBtnSelectAll={
                numSelected < rowsPerPage && !loading && useNewPagination
              }
              isShowBtnDeselectAll={
                numSelected === rowsPerPage && !loading && useNewPagination
              }
              handleSelectAll={handleSelectAll}
              selectedRequests={selectedRequests}
              onDeleteSelectedRequests={handleDeleteRequestConfirm}
            />
          )}
        </div>
        {!selectedRequests?.length && renderDesktopActionTable()}
      </div>
      <div id={TTS_GUIDE.REQUESTS_TABLE}>
        <RequestTable
          requests={requests}
          page={page}
          total={totalRequests}
          sort={sort}
          loading={loading}
          playable
          tableHeight={
            useNewDubbingUi
              ? `calc(80vh - ${showNotiOldVersion ? 68 : 12}px)`
              : `${MAX_BODY_CONTENT_HEIGHT}px`
          }
          selectedRequests={selectedRequests}
          isProcessBySeconds={isProcessDubbingByUnitSecond}
          isStudioHaveSubtitleFeature={isStudioHaveSubtitleFeature}
          onUpdateRequest={updateRequest}
          onChangeSort={handleChangeSort}
          onChangePage={handleChangePage}
          onSelectRequest={handleSelectRequest}
          onDeleteSelectedRequests={handleDeleteRequestConfirm}
          limit={pageLimit}
          onEnableEditor={() => {}}
          setNumSelected={setNumSelected}
          rowsPerPage={rowsPerPage}
          isExpand={isExpand}
          onChangeRowPage={setRowsPerPage}
          isScrolling={isScrolling}
        />
      </div>
      {selectedRequests?.length ? (
        <div className="select-request-popper" style={{ display: 'none' }}>
          <div className="">
            {t('selectRequests', { numRequests: selectedRequests.length })}
          </div>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteOutlineOutlined />}
            onClick={handleDeleteSelectRequest}
          >
            {t('delete')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            disabled={!allowDownload}
            startIcon={<DownloadOutlined />}
            onClick={handleDownloadAudios}
          >
            {t('download')}
          </Button>
          <IconButton color="secondary" onClick={() => handleSelectRequest([])}>
            <Close />
          </IconButton>
        </div>
      ) : null}
      <NotificationDialog
        name="ssml"
        title={t('deleteRequestTitle')}
        description={t('deleteRequestTitleDescription')}
        variant="warning"
        open={deleteWarning}
        onClose={handleCloseDeleteWarningDialog}
        actionComponent={renderConfirmDeleteRequestACtion(
          handleCloseDeleteWarningDialog,
          handleDeleteRequestConfirm,
        )}
      />
      <NotificationDialog
        name="ssml"
        title={t('exceedRequests')}
        description={t('validateRequestDescription', {
          maxRequests: MAX_REQUEST_DELETE,
        })}
        variant="warning"
        open={validateDeleteRequest}
        onClose={handleCloseValidateDeleteRequestDialog}
        actionComponent={renderCloseButton(
          handleCloseValidateDeleteRequestDialog,
        )}
      />
    </StyledRequests>
  );
};

export default ListDubbingNewVersion;
